import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { VehiclesHttpService } from './vehicles.http.service';
import { objectToCamel } from 'ts-case-convert';
import {
  IVehiclePayload,
  IVehicleScheduleAvailability,
  IVehicleScheduleFilters,
  IVehicleSchedulePage,
  IVehicleScheduleUpdate,
  IVehicleScheduleUpdateOutcome,
} from 'src/app/models';
import { ScheduleGroupBy } from 'src/app/models/enums';
import { AuthService } from '../auth/auth.service';
import { WebSocketService } from '../web-socket/web-socket.service';

@Injectable({
  providedIn: 'root',
})
export class VehiclesService {
  
  constructor(
    private vehiclesHttpService: VehiclesHttpService,
    private webSocketService: WebSocketService,
    private authService: AuthService,
  ) {}

  async getSetupVehicles(routeId: number) {
    const source$ = this.vehiclesHttpService.getSetupVehicles(routeId);
    const vehicles = await lastValueFrom(source$);
    return vehicles;
  }

  async getVehicleSchedule(
    page: number,
    size: number,
    from?: Date,
    to?: Date,
    groupBy?: ScheduleGroupBy,
    filters?: IVehicleScheduleFilters,
  ): Promise<IVehicleSchedulePage> {
    const source$ = this.vehiclesHttpService.getVehicleSchedule(page, size, from, to, groupBy, filters);
    const response = (await lastValueFrom(source$)) as { data?: object };
    const schedulePage = objectToCamel((response?.data ?? response ?? {}) as object) as IVehicleSchedulePage;
    return {
      ...schedulePage,
      content: (schedulePage.content ?? []).map((schedule) => ({
        ...schedule,
        availabilities: (schedule.availabilities ?? []).map((availability) =>
          this.toScheduleAvailability(availability),
        ),
      })),
    };
  }

  private toScheduleAvailability(availability: IVehicleScheduleAvailability): IVehicleScheduleAvailability {
    return {
      ...availability,
      startDateTime: new Date(availability.startDateTime),
      endDateTime: new Date(availability.endDateTime),
      trips: (availability.trips ?? []).map((trip) => ({
        ...trip,
        startDateTime: new Date(trip.startDateTime),
        endDateTime: new Date(trip.endDateTime),
      })),
    };
  }
  
  async filterVehicles(
    page: number,
    size: number,
    clientsSelected: number[],
    operatorsSelected: number[],
    active?: boolean,
    searchValue?: string
  ) {
    const source$ = this.vehiclesHttpService.filterVehicles(
      page, size, clientsSelected, operatorsSelected, active, searchValue,
    );
    const response: any = await lastValueFrom(source$);
    return response?.data;
  }

  async getVehicles(clientId?: number) {
    const source$ = this.vehiclesHttpService.getVehicles(clientId);
    const vehicles = await lastValueFrom(source$);
    return vehicles;
  }

  async getVehicle(vehicleId: number) {
    const source$ = this.vehiclesHttpService.getVehicle(vehicleId);
    const vehicle = await lastValueFrom(source$);
    return objectToCamel(vehicle as IVehiclePayload);
  }

  async createVehicle(payload: IVehiclePayload) {
    const source$ = this.vehiclesHttpService.createVehicle(payload);
    const vehicle = await lastValueFrom(source$);
    return objectToCamel(vehicle as IVehiclePayload);
  }

  async updateVehicle(vehicleId: number, payload: IVehiclePayload) {
    const source$ = this.vehiclesHttpService.updateVehicle(vehicleId, payload);
    const vehicle = await lastValueFrom(source$);
    return objectToCamel(vehicle as IVehiclePayload);
  }

  async applySchedule(
    update: IVehicleScheduleUpdate,
    onProgress?: (message: any) => void,
  ): Promise<IVehicleScheduleUpdateOutcome> {
    const operationId = await lastValueFrom(this.vehiclesHttpService.applySchedule(update));
    const ephemeralToken = (await this.authService.getEphemeralToken()) as string;
    const outcome = await this.webSocketService.trackOperation(
      operationId,
      ephemeralToken,
      onProgress,
      async () => (await this.authService.getEphemeralToken()) as string,
    );
    if (outcome.error) throw outcome.error;
    return objectToCamel((outcome.result ?? {}) as object) as unknown as IVehicleScheduleUpdateOutcome;
  }
}
